import { HttpException, orthancHttp } from '../http';

/**
 * Orthanc's REST API, not DICOMweb (§5.2). Two endpoints, one implemented consumer.
 *
 * Endpoints carry no leading slash, per M2's convention; HttpClient.buildUrl joins. That is
 * design defect 4 dying without being worked on: develop did `${this.baseUrl}/${endpoint}`
 * and passed leading-slash endpoints (PACSApiClient.ts:22,63), producing `{pacs}//tools/find`
 * -- and with no pacsUrl cookie baseUrl was '', making the URL the PROTOCOL-RELATIVE
 * `//tools/find`, which resolves to a host literally named `tools`. HttpClient.ts:99 strips
 * both sides and :89 records it.
 *
 * D5. Shaped against TWO consumers, because M4.1 D9 parked this client for exactly that
 * reason: the overlay readout and M4.3's series-download row both need findSeriesId(). The
 * download row's method is added below (M4.4) rather than reshaping this one:
 *
 *   export async function getSeriesArchive(orthancSeriesId: OrthancSeriesId): Promise<Blob>
 *
 * Not `Promise<ArrayBuffer>`, this comment's earlier reservation: utils.downloadBlob
 * (@ohif/core) takes a Blob, so `responseType: 'arraybuffer'` would force a second full
 * copy of the largest payload in the app.
 */
export type OrthancSeriesId = string;

/**
 * `null` is a legitimate answer, not a failure: the series is simply not in this Orthanc.
 * Measured 2026-08-13 -- a miss is `200 []`, never a 404.
 *
 * Transport errors are NOT swallowed (M4.1 D8). develop's fetchSeriesUIDFromSeriesInstanceUID
 * returned null from a catch (PACSApiClient.ts:33-36), so "the server 500'd" and "this series
 * is unknown here" were the same value -- and its fetchSeriesLabels then went on to request
 * /series/null/labels, a guaranteed 404 on every slice.
 */
export async function findSeriesId(seriesInstanceUid: string): Promise<OrthancSeriesId | null> {
  const { data, status } = await orthancHttp.post<unknown>('tools/find', {
    Level: 'Series',
    Query: { SeriesInstanceUID: seriesInstanceUid },
  });

  // `status`, not 0. exceptions.ts:12-17 reserves 0 for "no response at all", and
  // HttpClient.ts:46-54 records that HttpResponse.status exists so a caller rejecting a 2xx
  // body need not invent one. clients/viewer.ts's fetchOrganReviews is the precedent.
  if (!Array.isArray(data)) {
    throw new HttpException(
      status,
      `tools/find answered ${status} with a body that is not an array`
    );
  }

  const [first] = data;

  return typeof first === 'string' && first ? first : null;
}

/**
 * Narrowed at the boundary rather than asserted: develop wrote `response.data || []`
 * (PACSApiClient.ts:65) over a `string[]` the server was trusted to have sent. A non-string
 * row would reach the ORGAN_QA_LABELS filter and simply not match, so dropping it here costs
 * nothing and keeps the cache's type honest.
 */
export async function getSeriesLabels(orthancSeriesId: OrthancSeriesId): Promise<string[]> {
  const { data, status } = await orthancHttp.get<unknown>(
    `series/${encodeURIComponent(orthancSeriesId)}/labels`
  );

  if (!Array.isArray(data)) {
    throw new HttpException(
      status,
      `series labels answered ${status} with a body that is not an array`
    );
  }

  return data.filter((label): label is string => typeof label === 'string' && label.length > 0);
}

// A whole-series ZIP is orders of magnitude larger than any other call this
// client makes, and HttpClient's 15s default bounds the response body, not
// just the handshake -- it aborts mid-archive on any realistic series.
// Same override the uploader takes for develop's five-minute budget.
const ARCHIVE_TIMEOUT_MS = 5 * 60 * 1000;

export async function getSeriesArchive(orthancSeriesId: OrthancSeriesId): Promise<Blob> {
  const response = await orthancHttp.get<Blob>(
    `series/${encodeURIComponent(orthancSeriesId)}/archive`,
    { responseType: 'blob', timeout: ARCHIVE_TIMEOUT_MS }
  );
  return response.data;
}
